"use client";

import { useState } from "react";
import {
  Checkbox,
  CopyButton,
  DataTable,
  ErrorNotice,
  Notice,
  SelectField,
  StatCard,
  StatGrid,
  TextField,
} from "@/components/tool-kit/primitives";
import {
  BUTTON_SMALL,
  LABEL,
  WIDGET_H2,
  WIDGET_H3,
} from "@/components/tool-kit/styles";
import { VseprSketch } from "@/components/tools/chem-reference/vsepr-sketch";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { t, tPlural } from "@/lib/i18n/interpolate";
import {
  findShape,
  MAX_BONDED,
  MAX_LONE_PAIRS,
  notationSubscript,
  notationText,
  parseNotation,
  shapesForSteric,
  sketchPoints,
  type VseprShape,
} from "@/lib/tool-kit/chem-reference/vsepr";
import {
  formulaToUnicode,
  parseFormula,
} from "@/lib/tool-kit/chemistry/formula";

/** English strings. A translated content file overrides these through `ui`. */
export const VSEPR_GEOMETRY_FINDER_STRINGS = {
  bondedLabel: "Bonded atoms on the central atom (X)",
  bondedHint:
    "Count atoms, not bonds: a double or triple bond is still one bonded atom and one electron domain.",
  loneLabel: "Lone pairs on the central atom (E)",
  notationLabel: "Or type AXE notation",
  notationHint:
    "A is the central atom, X a bonded atom, E a lone pair: AX2E2 is water, AX4 is methane.",
  notationInvalid:
    "That is not AXE notation. Write A, then X and E with their counts, such as AX3E or AX₂E₂.",
  notationOutOfRange:
    "This tool covers up to 6 bonded atoms and 4 lone pairs, and a total of at most 6 electron domains.",
  tryLabel: "Try an example",
  sameAtomsLabel: "All the outer atoms are the same element",
  results: "Results",
  notation: "AXE notation",
  steric: "Steric number",
  electronGeometry: "Electron geometry",
  molecularShape: "Molecular shape",
  bondAngles: "Bond angles",
  hybridization: "Hybridization",
  polarity: "Polarity",
  geometryNames: {
    linear: "Linear",
    trigonalPlanar: "Trigonal planar",
    bent: "Bent",
    tetrahedral: "Tetrahedral",
    trigonalPyramidal: "Trigonal pyramidal",
    trigonalBipyramidal: "Trigonal bipyramidal",
    seesaw: "Seesaw",
    tShaped: "T-shaped",
    octahedral: "Octahedral",
    squarePyramidal: "Square pyramidal",
    squarePlanar: "Square planar",
  },
  hybridizationNames: {
    sp: "sp",
    sp2: "sp²",
    sp3: "sp³",
    sp3d: "sp³d",
    sp3d2: "sp³d²",
  },
  anglesIdeal: "{angles} (ideal values for this arrangement)",
  anglesCompressed:
    "{angles} ideal, slightly smaller in practice because lone pairs take up more room than bonding pairs",
  placementTrigonalBipyramid:
    "Lone pairs sit in equatorial positions, where each has only two neighbours at 90°.",
  placementOctahedron:
    "The two lone pairs sit opposite each other, 180° apart, which leaves the four atoms in one plane.",
  polarNonpolar:
    "Nonpolar when the outer atoms are identical: the bond dipoles cancel by symmetry.",
  polarMixedSymmetric:
    "Probably polar: the shape is symmetric, but different outer atoms mean the bond dipoles do not cancel.",
  polarAsymmetric:
    "Polar if the A–X bonds are polar: the shape is not symmetric, so the bond dipoles do not cancel.",
  polarityNote:
    "A guide only. It needs polar A–X bonds, so check the electronegativity difference.",
  diatomic:
    "With only one bonded atom the molecule is diatomic, so it is linear by definition. The electron geometry still follows the steric number.",
  noBonds:
    "Enter at least one bonded atom: VSEPR describes the atoms around a central atom.",
  tooMany:
    "This tool covers up to 6 electron domains in total. Seven-domain shapes such as IF₇ are beyond a standard course.",
  notListed:
    "No stable main-group molecule with this combination appears in standard VSEPR tables, so the tool does not name a shape for it.",
  examplesLabel: "Examples",
  sketchHeading: "Model",
  rotateLabel: "Rotate the model",
  sketchLabel: "3-D model of the molecule: {description}",
  sketchKey:
    "A is the central atom, X a bonded atom, and a dashed lobe with two dots is a lone pair.",
  sketchDescription: "{shape}, with {atoms} and {pairs} on the central atom",
  atomsCount: "one: {count} bonded atom|other: {count} bonded atoms",
  pairsCount: "one: {count} lone pair|other: {count} lone pairs",
  tableHeading: "All shapes with a steric number of {steric}",
  columnNotation: "Notation",
  columnLonePairs: "Lone pairs",
  columnShape: "Molecular shape",
  columnExamples: "Examples",
  copy: "Copy result",
  copied: "Copied!",
  copyText:
    "{notation}: electron geometry {electron}, molecular shape {shape}, bond angles {angles}, hybridization {hybrid}",
} as const;

type Strings = typeof VSEPR_GEOMETRY_FINDER_STRINGS;

const EXAMPLES = [
  { formula: "H2O", x: 2, e: 2 },
  { formula: "NH3", x: 3, e: 1 },
  { formula: "CH4", x: 4, e: 0 },
  { formula: "CO2", x: 2, e: 0 },
  { formula: "BF3", x: 3, e: 0 },
  { formula: "SO2", x: 2, e: 1 },
  { formula: "PCl5", x: 5, e: 0 },
  { formula: "SF4", x: 4, e: 1 },
  { formula: "ClF3", x: 3, e: 2 },
  { formula: "XeF2", x: 2, e: 3 },
  { formula: "SF6", x: 6, e: 0 },
  { formula: "BrF5", x: 5, e: 1 },
  { formula: "XeF4", x: 4, e: 2 },
] as const;

const BONDED_OPTIONS = Array.from({ length: MAX_BONDED }, (_, i) =>
  String(i + 1)
);
const LONE_OPTIONS = Array.from({ length: MAX_LONE_PAIRS + 1 }, (_, i) =>
  String(i)
);

/** "CO2" → "CO₂", "NO3-" → "NO₃⁻"; the raw text when it will not parse. */
function prettyFormula(raw: string): string {
  const parsed = parseFormula(raw);
  return parsed.ok ? formulaToUnicode(parsed.formula) : raw;
}

function polarityText(shape: VseprShape, same: boolean, strings: Strings) {
  if (!shape.symmetric) {
    return strings.polarAsymmetric;
  }
  return same ? strings.polarNonpolar : strings.polarMixedSymmetric;
}

function placementText(shape: VseprShape, strings: Strings): string | null {
  if (shape.e === 0) {
    return null;
  }
  if (shape.electronGeometry === "trigonalBipyramidal") {
    return strings.placementTrigonalBipyramid;
  }
  return shape.electronGeometry === "octahedral" && shape.e === 2
    ? strings.placementOctahedron
    : null;
}

function ShapeTable({
  shape,
  strings,
}: {
  shape: VseprShape;
  strings: Strings;
}) {
  const fmt = useToolNumber();
  const rows = shapesForSteric(shape.steric).map((s) => [
    notationSubscript(s.x, s.e),
    `${fmt(s.e)}`,
    strings.geometryNames[s.shape],
    s.examples.map(prettyFormula).join(", "),
  ]);
  return (
    <div className="space-y-3">
      <h3 className={WIDGET_H3}>
        {t(strings.tableHeading, { steric: fmt(shape.steric) })}
      </h3>
      <DataTable
        head={[
          strings.columnNotation,
          strings.columnLonePairs,
          strings.columnShape,
          strings.columnExamples,
        ]}
        rows={rows}
      />
    </div>
  );
}

function ShapeResults({ shape, same }: { shape: VseprShape; same: boolean }) {
  const strings = useToolUi(VSEPR_GEOMETRY_FINDER_STRINGS);
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const angles = shape.idealAngles.map((a) => `${fmt(a)}°`).join(", ");
  const angleText = t(
    shape.trend === "ideal" ? strings.anglesIdeal : strings.anglesCompressed,
    { angles }
  );
  const shapeName = strings.geometryNames[shape.shape];
  const electronName = strings.geometryNames[shape.electronGeometry];
  const hybrid = strings.hybridizationNames[shape.hybridization];
  const placement = placementText(shape, strings);
  const description = t(strings.sketchDescription, {
    shape: shapeName,
    atoms: tPlural(strings.atomsCount, locale, shape.x, {
      count: fmt(shape.x),
    }).trim(),
    pairs: tPlural(strings.pairsCount, locale, shape.e, {
      count: fmt(shape.e),
    }).trim(),
  });
  return (
    <>
      <StatGrid>
        <StatCard emphasis label={strings.molecularShape} value={shapeName} />
        <StatCard label={strings.electronGeometry} value={electronName} />
        <StatCard label={strings.hybridization} value={hybrid} />
        <StatCard
          label={strings.notation}
          value={notationSubscript(shape.x, shape.e)}
        />
        <StatCard label={strings.steric} value={fmt(shape.steric)} />
        <StatCard label={strings.bondAngles} value={angles} />
      </StatGrid>
      <ul className="list-disc space-y-1 pl-5 text-foreground text-sm">
        <li>{angleText}</li>
        {placement ? <li>{placement}</li> : null}
        <li>
          <span className="font-medium">{strings.polarity}: </span>
          {polarityText(shape, same, strings)} {strings.polarityNote}
        </li>
        <li>
          <span className="font-medium">{strings.examplesLabel}: </span>
          {shape.examples.map(prettyFormula).join(", ")}
        </li>
      </ul>
      <VseprSketch
        description={description}
        points={sketchPoints(shape)}
        strings={strings}
      />
      <ShapeTable shape={shape} strings={strings} />
      <CopyButton
        copiedLabel={strings.copied}
        label={strings.copy}
        text={t(strings.copyText, {
          notation: notationText(shape.x, shape.e),
          electron: electronName,
          shape: shapeName,
          angles,
          hybrid,
        })}
      />
    </>
  );
}

function Outcome({ x, e, same }: { x: number; e: number; same: boolean }) {
  const strings = useToolUi(VSEPR_GEOMETRY_FINDER_STRINGS);
  const result = findShape(x, e);
  if (result.status === "ok") {
    return <ShapeResults same={same} shape={result.shape} />;
  }
  if (result.status === "diatomic") {
    return (
      <Notice>
        {strings.diatomic} {strings.electronGeometry}:{" "}
        {strings.geometryNames[result.electronGeometry]}.
      </Notice>
    );
  }
  return <ErrorNotice>{strings[result.reason]}</ErrorNotice>;
}

export default function VseprGeometryFinderWidget() {
  const strings = useToolUi(VSEPR_GEOMETRY_FINDER_STRINGS);
  const [x, setX] = useState(2);
  const [e, setE] = useState(2);
  const [notation, setNotation] = useState(notationText(2, 2));
  const [same, setSame] = useState(true);
  const parsed = parseNotation(notation);
  const outOfRange =
    parsed !== null && (parsed.x > MAX_BONDED || parsed.e > MAX_LONE_PAIRS);

  function setCounts(nextX: number, nextE: number) {
    setX(nextX);
    setE(nextE);
    setNotation(notationText(nextX, nextE));
  }

  function typeNotation(value: string) {
    setNotation(value);
    const next = parseNotation(value);
    if (next && next.x <= MAX_BONDED && next.e <= MAX_LONE_PAIRS) {
      setX(next.x);
      setE(next.e);
    }
  }

  let notationProblem: string | null = null;
  if (notation.trim() !== "" && parsed === null) {
    notationProblem = strings.notationInvalid;
  } else if (outOfRange) {
    notationProblem = strings.notationOutOfRange;
  }

  return (
    <>
      <div className="grid gap-4 sm:grid-cols-2">
        <SelectField
          hint={strings.bondedHint}
          id="vsepr-x"
          label={strings.bondedLabel}
          onChange={(value) => setCounts(Number(value), e)}
          options={BONDED_OPTIONS.map((value) => ({ value, label: value }))}
          value={String(x)}
        />
        <SelectField
          id="vsepr-e"
          label={strings.loneLabel}
          onChange={(value) => setCounts(x, Number(value))}
          options={LONE_OPTIONS.map((value) => ({ value, label: value }))}
          value={String(e)}
        />
      </div>
      <TextField
        hint={strings.notationHint}
        id="vsepr-notation"
        label={strings.notationLabel}
        mono
        onChange={typeNotation}
        value={notation}
      />
      {notationProblem ? <ErrorNotice>{notationProblem}</ErrorNotice> : null}
      <div className="flex flex-wrap items-center gap-2">
        <span className={LABEL}>{strings.tryLabel}</span>
        {EXAMPLES.map((example) => (
          <button
            className={BUTTON_SMALL}
            key={example.formula}
            onClick={() => setCounts(example.x, example.e)}
            type="button"
          >
            {prettyFormula(example.formula)}
          </button>
        ))}
      </div>
      <Checkbox
        checked={same}
        label={strings.sameAtomsLabel}
        onChange={setSame}
      />
      <section aria-live="polite" className="space-y-4">
        <h2 className={WIDGET_H2}>{strings.results}</h2>
        <Outcome e={e} same={same} x={x} />
      </section>
    </>
  );
}
